import getAnalysisFlagSerie from "../../Chart/series/getAnalysisFlagSerie";
import getQuantityFlagSerie from "../../Chart/series/getQuantityFlagSerie";
import type { SelectedQuantity } from "../../useSelectedQuantities";
import { quantityColor } from "../../useSelectedQuantities";
import { getAxisId } from "../../Chart/hooks/useQuantityYAxes";
import type { GapPointMetadata } from "../../Chart/PointMetadata";
import { GAP_POINT_METADATA } from "../../Chart/PointMetadata";
import { NORMALIZED_GAP_Y_AXIS } from "../NormalizedGap/useOptions";
import { color, lightest } from "@ui";
import t from "../../../../i18n";
import type { ActiveAnalysisQuantityValue } from "../../../../api/useActiveAnalysesStream";
import type { SeriesArearangeOptions, SeriesLineOptions } from "highcharts";
import type { NonEmptyArray } from "diagnostics-lib/array";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import get2SigmaSerie from "../get2SigmaSerie";
import type { Setup } from "../../../../api/useSetups";
import isSymptom from "../isSymptom";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import getQuantityOffset from "../../../../api/getQuantityOffset";
import getQuantitySigma from "../../../../api/getQuantitySigma";
import type { Project } from "../../../../api/useProjects";
import type { MetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import type { QuantityIndex, Quantity } from "../../../../api/useQuantities";
import { isAnalysisQuantityValid } from "../../metrology";
import { computeQuantityGap, computeQuantityNormalizedGap } from "diagnostics-domain/quantity/gap";
export const SYMPTOMS_COLOR = color.raw.oxygen400;

function getPointOptions(
  analysis: ActiveAnalysis,
  quantityValue: ActiveAnalysisQuantityValue,
  normalized: boolean,
  setups: Setup[],
  quantity: Quantity,
) {
  const analysisSetup = getAnalysisSetup(setups, analysis);
  const offset = getQuantityOffset(analysisSetup, quantity.id);
  const sigma = getQuantitySigma(analysisSetup, quantity.id);
  const y = normalized
    ? computeQuantityNormalizedGap(quantityValue.measured, quantityValue.simulated, offset, sigma)
    : computeQuantityGap(quantityValue.measured, quantityValue.simulated, offset);
  const gapReal = computeQuantityGap(quantityValue.measured, quantityValue.simulated, offset);
  const isSymptomValue = isSymptom(quantityValue, offset, sigma);

  const pointMetadata: GapPointMetadata = {
    type: GAP_POINT_METADATA,
    group: analysis.group,
    label: analysis.label,
    human:
      gapReal === null || gapReal === 0
        ? null
        : {
            value: gapReal,
            unit: quantity.unit,
            precision: 2,
            sign: true,
          },
    isSymptom: isSymptomValue,
  };

  if (y === null)
    return {
      id: `analysis-id-${analysis.id}`,
      x: analysis.date,
      y: null,
      custom: pointMetadata,
    };

  if (isSymptomValue)
    return {
      id: `analysis-id-${analysis.id}`,
      x: analysis.date,
      y,
      custom: pointMetadata,
      description: t("symptoms.gap.chart.symptom"),
      marker: {
        fillColor: lightest(SYMPTOMS_COLOR),
        lineColor: SYMPTOMS_COLOR,
      },
    };

  return {
    id: `analysis-id-${analysis.id}`,
    x: analysis.date,
    y,
    custom: pointMetadata,
    description: t("symptoms.gap.chart.noSymptom"),
  };
}

export function getPoints(
  analyses: ActiveAnalysis[],
  quantity: Quantity,
  normalized: boolean,
  setups: Setup[],
  _metrologyOverrides: MetrologyOverrides,
) {
  return analyses.reduce<Highcharts.PointOptionsObject[]>((pointArray, analysis, index) => {
    const previousAnalysis = analyses[index - 1];
    const quantityValue = analysis.quantityValues[quantity.id];

    if (quantityValue === undefined) {
      const custom: GapPointMetadata = {
        type: GAP_POINT_METADATA,
        group: null,
        label: null,
        human: null,
        isSymptom: null,
      };
      pointArray.push({
        id: `analysis-id-${analysis.id}`,
        x: analysis.date,
        y: null,
        custom,
      });
      return pointArray;
    }

    if (!isAnalysisQuantityValid(analysis, quantity)) {
      const custom: GapPointMetadata = {
        type: GAP_POINT_METADATA,
        group: null,
        label: null,
        human: null,
        isSymptom: null,
      };
      pointArray.push({
        id: `analysis-id-${analysis.id}`,
        x: analysis.date,
        y: null,
        custom,
      });
      return pointArray;
    }

    if (previousAnalysis !== undefined && previousAnalysis.group !== analysis.group) {
      const custom: GapPointMetadata = {
        type: GAP_POINT_METADATA,
        group: null,
        label: null,
        human: null,
        isSymptom: null,
      };

      pointArray.push({
        id: `intercycle-${previousAnalysis.id}-${analysis.id}`,
        x: (previousAnalysis.date + analysis.date) / 2,
        y: null,
        allowPointSelect: false,
        custom,
      });
    }

    pointArray.push(getPointOptions(analysis, quantityValue, normalized, setups, quantity));

    return pointArray;
  }, []);
}

function getGapSeries({
  analyses,
  selectedQuantities,
  normalized,
  setups,
  metrologyOverrides,
}: {
  analyses: ActiveAnalysis[];
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
  normalized: boolean;
  setups: Setup[];
  metrologyOverrides: MetrologyOverrides;
}): Highcharts.SeriesLineOptions[] {
  return selectedQuantities.map(selectedQuantity => ({
    type: "line",
    id: `gap-serie-id-${selectedQuantity.quantity.name}`,
    name: selectedQuantity.quantity.name,
    data: getPoints(analyses, selectedQuantity.quantity, normalized, setups, metrologyOverrides),
    yAxis: normalized
      ? NORMALIZED_GAP_Y_AXIS
      : getAxisId(selectedQuantities[0].quantity, false, false),
    cursor: "default",
    replicateInNavigator: true,
    color: quantityColor(selectedQuantity.colorIndex),
    marker: {
      symbol: "circle",
      lineWidth: 2,
      fillColor: lightest(quantityColor(selectedQuantity.colorIndex)),
      lineColor: quantityColor(selectedQuantity.colorIndex),
    },
  }));
}

function getSeries(
  project: Project,
  analyses: ActiveAnalysis[],
  selectedQuantities: NonEmptyArray<SelectedQuantity>,
  quantityIndex: QuantityIndex,
  normalized: boolean,
  setups: Setup[],
  publicationFlagDisplayed: boolean,
  chartType: "gap" | "normalizedGap",
  metrologyOverrides: MetrologyOverrides,
  displayInvalidPoints: boolean,
): (SeriesLineOptions | SeriesArearangeOptions | Highcharts.SeriesScatterOptions)[] {
  const gapSeries = getGapSeries({
    analyses,
    selectedQuantities,
    normalized,
    setups,
    metrologyOverrides,
  });
  const analysisFlagSerie = getAnalysisFlagSerie({
    project,
    setups,
    analyses,
    quantityIndex,
    chartType: "SYMPTOMS",
    publicationFlagDisplayed,
  });
  const quantitiesFlagSerie = getQuantityFlagSerie({
    analyses,
    quantities: selectedQuantities.map(cQ => cQ.quantity),
    withSymptomFlags: true,
    chartType,
    setups,
    metrologyOverrides,
    displayInvalidPoints,
  });
  const sigmaSerie = get2SigmaSerie({
    analyses,
    selectedQuantities,
    isGap: true,
    normalized,
    setups,
    metrologyOverrides,
    displayInvalidPoints,
  });

  if (!analysisFlagSerie.data) throw new Error("Missing analysisFlagSerie.data");

  return [quantitiesFlagSerie, sigmaSerie, ...gapSeries, analysisFlagSerie];
}

export default getSeries;
